import { ArrowRight, Inbox, Rocket, Settings } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { cn } from '@/lib/utils'
import { useProfile } from '@/api/profile'
import { useInbox } from '@/api/inbox'
import { useUIStore } from '@/stores/useUIStore'

/**
 * Accueil du manager en essai, à la place de « Sélectionnez une conversation » :
 * c'est souvent son premier écran après l'inscription. Il y apprend les deux
 * espaces (ici le Téléphone, les réglages dans l'Administration). L'appelant
 * le réserve au manager dont la mise en route n'est pas finie
 * (useMiseEnRouteAFinir) et qui ne l'a pas masqué. Boîte vide : pas de
 * conversation à choisir, on propose le premier appel (onComposer ouvre le clavier).
 */
export function AccueilEssai({ darkMode, onComposer }: { darkMode: boolean; onComposer: () => void }) {
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const navigate = useNavigate()
  const { data: profile } = useProfile()
  const masquer = useUIStore((s) => s.masquerAccueilEssai)
  const prenom = profile?.first_name?.trim()
  const userId = profile?.id
  // Même requête que la liste à l'ouverture, sans filtre ni recherche (déjà en cache).
  const boite = useInbox('all', '', null)
  const boiteVide = boite.isSuccess && (boite.data.pages[0]?.items.length ?? 0) === 0

  const espaces = [
    {
      icone: Inbox,
      titre: t('manager.myPhone', 'Téléphone'),
      repere: t('inbox.welcome.phoneHere', 'Vous êtes ici'),
      texte: t('inbox.welcome.phoneText', 'Votre espace de travail : appeler, répondre et suivre chaque client.'),
      ici: true,
    },
    {
      icone: Settings,
      titre: t('sidebar.configuration', 'Administration'),
      repere: t('inbox.welcome.adminWhere', 'En bas à gauche'),
      texte: t('inbox.welcome.adminText', 'Votre espace de gestion : tout ce qui se configure, des numéros aux agents IA.'),
      ici: false,
    },
  ]

  return (
    <div className="w-full max-w-md px-6 text-center">
      <div
        className={cn(
          'mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-2xl ring-1',
          darkMode ? 'bg-indigo-500/10 ring-indigo-500/30' : 'bg-blue-50 ring-blue-200',
        )}
      >
        <Rocket className={cn('h-5 w-5', darkMode ? 'text-indigo-300' : 'text-blue-600')} />
      </div>
      <h2 className={cn('text-xl font-semibold tracking-tight', darkMode ? 'text-white' : 'text-slate-900')}>
        {prenom
          ? t('inbox.welcome.titleNamed', 'Bienvenue chez Callizy, {{name}}', { name: prenom })
          : t('inbox.welcome.title', 'Bienvenue chez Callizy')}
      </h2>
      <p className={cn('mt-2 text-sm leading-relaxed text-balance', darkMode ? 'text-slate-400' : 'text-slate-500')}>
        {t('inbox.welcome.body', 'Votre ligne est active : vous pouvez déjà appeler et recevoir des appels.')}
      </p>

      {/* Les deux espaces, avec les mêmes icônes que la barre latérale */}
      <ul
        className={cn(
          'mt-6 rounded-xl border text-left divide-y',
          darkMode ? 'border-slate-800 bg-slate-900/60 divide-slate-800' : 'border-slate-200 bg-white divide-slate-100',
        )}
      >
        {espaces.map((e) => {
          const Icone = e.icone
          return (
            <li key={e.titre} className="flex items-start gap-3 px-4 py-3">
              <Icone className={cn('h-4 w-4 mt-0.5 shrink-0', darkMode ? 'text-slate-400' : 'text-slate-500')} />
              <div className="min-w-0">
                <p className="flex items-center gap-2 text-sm">
                  <span className={cn('font-semibold', darkMode ? 'text-white' : 'text-slate-900')}>{e.titre}</span>
                  <span
                    className={cn(
                      'text-[11px]',
                      e.ici
                        ? cn('rounded-full px-1.5 py-0.5 font-medium leading-none', darkMode ? 'bg-indigo-500/15 text-indigo-300' : 'bg-blue-50 text-blue-700')
                        : darkMode ? 'text-slate-500' : 'text-slate-400',
                    )}
                  >
                    {e.repere}
                  </span>
                </p>
                <p className={cn('mt-0.5 text-[13px]', darkMode ? 'text-slate-400' : 'text-slate-500')}>{e.texte}</p>
              </div>
            </li>
          )
        })}
      </ul>

      <button
        type="button"
        onClick={() => navigate(`/${locale}/manager`)}
        className={cn(
          'mt-6 inline-flex h-10 items-center gap-2 rounded-lg px-4 text-sm font-semibold text-white shadow-sm transition-colors',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
          darkMode
            ? 'bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-400'
            : 'bg-[#0A192F] hover:bg-[#0A192F]/90 focus-visible:outline-[#0A192F]',
        )}
      >
        {t('inbox.welcome.cta', 'Voir les prochains pas')}
        <ArrowRight className="h-4 w-4" />
      </button>
      <p className={cn('mt-4 text-xs', darkMode ? 'text-slate-500' : 'text-slate-400')}>
        {boiteVide ? (
          <button
            type="button"
            onClick={onComposer}
            className={cn(
              'font-medium underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none',
              darkMode ? 'text-indigo-300 hover:text-indigo-200' : 'text-blue-600 hover:text-blue-700',
            )}
          >
            {t('inbox.welcome.hintEmpty', 'Ou passez votre premier appel')}
          </button>
        ) : (
          t('inbox.welcome.hint', 'Ou choisissez une conversation dans la liste.')
        )}
        {userId != null && (
          <>
            <span className="mx-1.5">·</span>
            <button
              type="button"
              onClick={() => masquer(userId)}
              className={cn(
                'underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none',
                darkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700',
              )}
            >
              {t('inbox.welcome.hide', 'Masquer')}
            </button>
          </>
        )}
      </p>
    </div>
  )
}
